import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Toggletip } from './Toggletip'
import './compare.css'

// One table for everything the console compares (release 3.3, the mentor's
// point 7: "too verbose; the comparison sheet").
//
// # WHAT IT REPLACES
//
// The screens that set one thing beside another were stacks of cards: the
// assessment review (74 questions, about 7,400 px), the contract clause
// reading (a 250 px card a clause, the same warning on every card), the
// questionnaire version compare (a list per change type under a sentence
// each). They become rows: the item first, one column per source, a status
// word, and the action.
//
// # THE RULES IT CARRIES
//
// - The item is the first column and is a button: it opens the row's whole
//   story in a drawer (the caller's), so nothing has to be read in the table.
// - A long cell is two lines, with "More" when it does not fit; More is a
//   button, so the keyboard reaches it, and it opens that cell in place.
// - Rows are grouped (a questionnaire's sections, a contract's schedules); a
//   group folds, and its header says how much is in it when folded.
// - The filters say how many rows each holds, the first is what needs
//   somebody, and the one chosen is the caller's to keep in the address.
// - An explanation is said once, behind the (i) on a column's header, never on
//   every row.
// - The table scrolls inside its own panel with the header kept, so the page
//   stays one window tall however many rows there are (the page budget, 7 Oct
//   plan 2.1); the panel is a named region that takes focus, so the keyboard
//   can scroll it.

export type CompareColumn<R> = {
  key: string
  label: string
  // A column of buttons has a name for screen readers and none on screen.
  hideLabel?: boolean
  // A sentence about the column, behind an (i) on its header.
  info?: string
  cell: (row: R) => ReactNode
  // The cell is free text: two lines, with More when it does not fit.
  clamp?: boolean
  // A class on the header and every cell, for the column's width.
  className?: string
}

export type CompareGroup<R> = {
  key: string
  title: string
  // What the header says beside the title ("4 of 4 answered, 1 to review").
  summary?: string
  rows: R[]
}

export type CompareFilter<K extends string> = { key: K; label: string; count: number }

export function CompareTable<R, K extends string>({
  label,
  columns,
  groups,
  rowKey,
  itemLabel,
  onOpen,
  openKey,
  rowClass,
  filters,
  filter,
  onFilter,
  folded,
  onFold,
  empty,
  tools,
  maxHeight = 'calc(100vh - 260px)',
}: {
  label: string
  // The first column: the item's short key, drawn as the button that opens it.
  columns: CompareColumn<R>[]
  groups: CompareGroup<R>[]
  rowKey: (row: R) => string
  itemLabel: (row: R) => { key: string; label: string }
  onOpen?: (row: R) => void
  // The row whose drawer is open, marked as current.
  openKey?: string | null
  rowClass?: (row: R) => string | undefined
  filters?: CompareFilter<K>[]
  filter?: K
  onFilter?: (next: K) => void
  folded?: ReadonlySet<string>
  onFold?: (group: string, fold: boolean) => void
  empty: ReactNode
  tools?: ReactNode
  maxHeight?: string
}) {
  const span = columns.length + 1
  const style: CSSProperties = { maxHeight }
  return (
    <div className="ct">
      {(filters || tools) && (
        <div className="ct-bar">
          {filters && (
            <div className="ct-filters" role="group" aria-label={'Show ' + label.toLowerCase()}>
              {filters.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className="ct-filter"
                  aria-pressed={f.key === filter}
                  onClick={() => onFilter?.(f.key)}
                >
                  {f.label}
                  <span className="ct-n">{f.count}</span>
                </button>
              ))}
            </div>
          )}
          {tools && <div className="ct-tools">{tools}</div>}
        </div>
      )}
      <div className="ct-scroll" role="region" aria-label={label} tabIndex={0} style={style}>
        <table className="ct-table">
          <thead>
            <tr>
              <th scope="col" className="ct-key">
                Key
              </th>
              {columns.map((c) => (
                <th scope="col" key={c.key} className={c.className}>
                  <span className={c.hideLabel ? 'vh' : 'ct-th'}>
                    {c.label}
                    {c.info && <Toggletip label={'About ' + c.label.toLowerCase()}>{c.info}</Toggletip>}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          {groups.map((g) => {
            const shut = folded?.has(g.key) ?? false
            return (
              <tbody key={g.key} className="ct-group">
                <tr className="ct-grouprow">
                  <th scope="rowgroup" colSpan={span}>
                    <button
                      type="button"
                      className="ct-fold"
                      aria-expanded={!shut}
                      onClick={() => onFold?.(g.key, !shut)}
                      disabled={!onFold}
                    >
                      <span className="ct-chev" aria-hidden="true" />
                      <span className="ct-gtitle">{g.title}</span>
                      {g.summary && <span className="ct-gsum">{g.summary}</span>}
                      {shut && <span className="ct-gsum">{g.rows.length + (g.rows.length === 1 ? ' row' : ' rows')}</span>}
                    </button>
                  </th>
                </tr>
                {!shut &&
                  g.rows.map((r) => {
                    const id = rowKey(r)
                    const item = itemLabel(r)
                    const cls = ['ct-row', rowClass?.(r), openKey === id ? 'ct-current' : ''].filter(Boolean).join(' ')
                    return (
                      <tr key={id} className={cls} data-row={id}>
                        <th scope="row" className="ct-key">
                          {onOpen ? (
                            <button type="button" className="ct-open" onClick={() => onOpen(r)} aria-label={item.label}>
                              {item.key}
                            </button>
                          ) : (
                            <span className="ct-open">{item.key}</span>
                          )}
                        </th>
                        {columns.map((c) => (
                          <td key={c.key} className={c.className}>
                            {c.clamp ? <Clamp>{c.cell(r)}</Clamp> : c.cell(r)}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
              </tbody>
            )
          })}
        </table>
        {groups.length === 0 && <div className="ct-empty">{empty}</div>}
      </div>
    </div>
  )
}

// Two lines of a long cell, and More when it does not fit. The measure runs
// after drawing and again when the cell's width changes; a cell that fits gets
// no button.
export function Clamp({ children }: { children: ReactNode }) {
  const box = useRef<HTMLSpanElement>(null)
  const [over, setOver] = useState(false)
  const [open, setOpen] = useState(false)
  useLayoutEffect(() => {
    const el = box.current
    if (!el || open) return
    const measure = () => setOver(el.scrollHeight > el.clientHeight + 1)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [open, children])
  return (
    <span className="ct-cell">
      <span ref={box} className={open ? 'ct-clamp ct-open-text' : 'ct-clamp'}>
        {children}
      </span>
      {(over || open) && (
        <button type="button" className="ct-more" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? 'Less' : 'More'}
        </button>
      )}
    </span>
  )
}
